// Invite a teammate into the operator org (COL-353). Thin UI over the
// operator-invite-member edge function, which is super_admin-gated and only
// ever writes the 'operator' app_role + an 'admin' membership in the caller's
// own org (the invariant lives server-side; this form can't choose a role).
//
// Only the org's super_admin sees the form — admins get the same read-only
// note as OperatorContactCard. Server RLS/role checks are the real gate.
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { useOperator } from "@/hooks/useOperator";
import { useLanguage } from "@/contexts/LanguageContext";
import { edgeFunctionErrorMessage } from "@/lib/edge-function-error";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UserPlus } from "lucide-react";

const isEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

export function OperatorInviteMemberCard() {
  const { operator } = useOperator();
  const { language } = useLanguage();
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  if (!operator) return null;
  if (operator.member_role !== "super_admin") {
    return (
      <p className="text-sm text-muted-foreground">
        Only your organization's super admin can invite teammates.
      </p>
    );
  }

  const send = async () => {
    const cleanName = name.trim();
    const cleanEmail = email.trim();
    if (!cleanName) return toast.error("Please enter the teammate's name.");
    if (!isEmail(cleanEmail))
      return toast.error("Please enter a valid email address.");

    setSending(true);
    try {
      // Non-2xx (403 not-super-admin, 409 email belongs to another org, …) is
      // collapsed by invoke into a generic error; edgeFunctionErrorMessage
      // unwraps the real reason from the response body.
      const res = await supabase.functions.invoke("operator-invite-member", {
        body: { email: cleanEmail, name: cleanName, language },
      });
      if (res.error)
        throw new Error(
          await edgeFunctionErrorMessage(res.error, "Could not send invitation")
        );

      toast.success(
        res.data?.existed
          ? "Existing user added to your team — invitation re-sent."
          : "Invitation sent — they'll get an email to set up their account."
      );
      setName("");
      setEmail("");
      // New member joins as an org 'admin' → refresh the roster.
      queryClient.invalidateQueries({ queryKey: ["operator-team-roster"] });
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Could not send invitation"
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <UserPlus className="h-4 w-4" aria-hidden />
          Invite a teammate
        </CardTitle>
        <CardDescription>
          They join as an <strong>Admin</strong> with day-to-day access to your
          properties, tenants and owners. They'll receive an email to set up
          their account.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="op-invite-name">Name</Label>
            <Input
              id="op-invite-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Jane Doe"
              disabled={sending}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="op-invite-email">Email</Label>
            <Input
              id="op-invite-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="jane@yourcompany.de"
              autoComplete="off"
              disabled={sending}
            />
          </div>
        </div>
        <Button onClick={send} disabled={sending} size="sm">
          {sending ? "Sending…" : "Send invitation"}
        </Button>
      </CardContent>
    </Card>
  );
}
